if (!customElements.get('predictive-search')) {
  class JerseyPredictiveSearch extends HTMLFormElement {
    constructor() {
      super();
      this.cache = new Map();
      this.requestVersion = 0;
      this.activeIndex = -1;
      this.onInput = this.handleInput.bind(this);
      this.onFocus = this.handleFocus.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onReset = (event) => { event.preventDefault(); this.clear(); };
      this.onOutside = (event) => { if (!this.contains(event.target)) this.close(); };
      this.onFocusOut = (event) => { if (!this.contains(event.relatedTarget)) this.close(); };
    }

    connectedCallback() {
      if (this.ready) return;
      this.input = this.querySelector('input[type="search"]');
      this.content = this.querySelector('.search__content');
      this.resultsElement = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-search-status]');
      if (!this.input || !this.content || !this.resultsElement) return;
      this.ready = true;
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.onFocus);
      this.input.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('reset', this.onReset);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('pointerdown', this.onOutside);
      this.setLoading(false);
    }

    disconnectedCallback() {
      this.invalidate();
      this.input?.removeEventListener('input', this.onInput);
      this.input?.removeEventListener('focus', this.onFocus);
      this.input?.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('reset', this.onReset);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('pointerdown', this.onOutside);
      if (this.closest('.template-search')) document.body.classList.remove('predictive-search-open');
      this.ready = false;
    }

    get query() {
      return this.input.value.trim().normalize('NFC');
    }

    invalidate() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.requestVersion += 1;
    }

    handleInput() {
      this.invalidate();
      this.close(false);
      this.setLoading(false);
      if (!this.query) { this.showRecommendations(); return; }
      this.timer = setTimeout(() => this.search(), 250);
    }

    handleFocus() {
      if (!this.query) { this.showRecommendations(); return; }
      clearTimeout(this.timer);
      this.search();
    }

    clear() {
      this.invalidate();
      this.input.value = '';
      this.setLoading(false);
      this.close(false);
      this.showRecommendations();
      this.input.focus();
    }

    showRecommendations() {
      this.content.classList.toggle('hidden', this.dataset.searchRecommendationEmpty === 'true');
      if (this.closest('.template-search')) {
        document.body.classList.toggle('predictive-search-open', this.dataset.searchRecommendationEmpty !== 'true');
      }
      if (this.status) this.status.textContent = '';
    }

    close(cancel = true) {
      if (cancel) this.invalidate();
      this.removeAttribute('results');
      this.removeAttribute('error');
      this.input?.setAttribute('aria-expanded', 'false');
      this.input?.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      this.options?.forEach((option) => option.setAttribute('aria-selected', 'false'));
      this.content?.classList.add('hidden');
      if (this.closest('.template-search')) document.body.classList.remove('predictive-search-open');
      this.setLoading(false);
      if (this.status) this.status.textContent = '';
    }

    setLoading(loading) {
      this.toggleAttribute('loading', loading);
      this.resultsElement?.setAttribute('aria-busy', String(loading));
      if (loading && this.status) this.status.textContent = this.dataset.loadingText;
    }

    setupURL() {
      const url = new URL(FoxTheme.routes.predictive_search_url, window.shopUrl || location.origin);
      url.searchParams.set('q', this.query);
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || '6');
      url.searchParams.set('resources[limit_scope]', 'all');
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
      return url.toString();
    }

    async search() {
      if (!this.query) { this.clear(); return; }
      this.invalidate();
      const version = this.requestVersion;
      const query = this.query;
      const url = this.setupURL();
      this.controller = new AbortController();
      this.removeAttribute('error');
      this.setLoading(true);
      try {
        let responseText = this.cache.get(url);
        if (responseText === undefined) {
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error('Predictive search request failed');
          responseText = await response.text();
        }
        if (!this.isConnected || version !== this.requestVersion || query !== this.query) return;
        this.render(responseText);
        this.cache.set(url, responseText);
        if (this.cache.size > 30) this.cache.delete(this.cache.keys().next().value);
      } catch (error) {
        if (error.name === 'AbortError' || version !== this.requestVersion || !this.isConnected) return;
        this.close(false);
        this.setAttribute('error', '');
        if (this.status) this.status.textContent = this.dataset.errorText;
      } finally {
        if (version === this.requestVersion) this.setLoading(false);
      }
    }

    render(responseText) {
      const parsed = new DOMParser().parseFromString(responseText, 'text/html');
      const source = parsed.getElementById(this.resultsElement.id);
      if (!source) throw new Error('Predictive search results are missing');
      this.resultsElement.innerHTML = source.innerHTML;
      this.options = [...this.resultsElement.querySelectorAll('[role="option"]')].filter((option) => this.optionLink(option));
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.input.setAttribute('aria-expanded', String(this.options.length > 0));
      this.setAttribute('results', '');
      this.content.classList.remove('hidden');
      if (this.closest('.template-search')) document.body.classList.add('predictive-search-open');
      if (this.status) {
        this.status.textContent = this.options.length ? this.dataset.resultsText.replace('[count]', String(this.options.length)) : '';
      }
    }

    optionLink(option) {
      return option.querySelector('.product-card__title a') || option.querySelector('a[href]');
    }

    handleKeyDown(event) {
      if (event.key === 'Escape' && this.hasAttribute('results')) {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (!this.hasAttribute('results') || !this.options?.length || event.isComposing) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        if (this.activeIndex === -1) this.activeIndex = delta === 1 ? 0 : this.options.length - 1;
        else this.activeIndex = (this.activeIndex + delta + this.options.length) % this.options.length;
        this.options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeIndex)));
        const active = this.options[this.activeIndex];
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView?.({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        this.optionLink(this.options[this.activeIndex]).click();
      }
    }
  }
  customElements.define('predictive-search', JerseyPredictiveSearch, { extends: 'form' });
}

if (!customElements.get('search-drawer')) {
  customElements.define('search-drawer', class extends DrawerComponent {
    get requiresBodyAppended() { return false; }
    get input() { return this.querySelector('input[type="search"]'); }
    get focusElement() { return this.input; }
  });
}
